// One public key as a section of the Security page (consistency-pass spec
// §3): label, type, fingerprint and last use, read-only until Edit. Editing
// renames it; the danger strip removes it — refused up front for the last
// key while signing is required, which the server refuses too.

import { Badge, Button, Confirm, toast } from "@pej/kore/components";
import { Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/api/error";
import { useCreateContextKey, useDeleteContextKey, usePatchContextKey } from "@/api/security";
import type { ContextKey } from "@/api/types";
import { toastFormError, toastMutationError } from "@/components/error-toast";
import { DangerStrip } from "@/components/section-page/danger-zone";
import { SectionCard } from "@/components/section-page/section-card";
import { useSectionEdit } from "@/components/section-page/section-edit";
import { Form, useKoreForm } from "@/lib/forms";
import {
    KeyFields,
    type KeyValues,
    keySchema,
    type LabelValues,
    labelSchema,
    NEW_KEY_DEFAULTS,
    whenUsed,
} from "./key-fields";
import { SECURITY_SECTION } from "./security-sections";

export interface KeySectionProps {
    workspaceId: string;
    /** null for the draft "New key". */
    contextKey: ContextKey | null;
    /** Set when removing this key is refused up front; shown beside a disabled Remove key. */
    removeRefusal?: string;
    onDraftDone?: () => void;
}

export function KeySection({ workspaceId, contextKey, removeRefusal, onDraftDone }: KeySectionProps) {
    const isDraft = contextKey === null;
    const createKey = useCreateContextKey(workspaceId);
    const patchKey = usePatchContextKey(workspaceId);
    const deleteKey = useDeleteContextKey(workspaceId);
    const [confirming, setConfirming] = useState(false);
    // A saved key's form holds and checks its label only (labelSchema); a
    // draft's holds all three fields (keySchema).
    const defaults: LabelValues | KeyValues = contextKey ? { label: contextKey.label } : NEW_KEY_DEFAULTS;
    const form = useKoreForm({ schema: contextKey ? labelSchema : keySchema, defaultValues: defaults });
    const sectionId = isDraft ? SECURITY_SECTION.newKey : SECURITY_SECTION.key(contextKey.id);
    // From the SAVED key, never the form: useSectionEdit re-registers when
    // the title changes, and re-registering ends the edit.
    const title = contextKey?.label ?? "New key";
    const edit = useSectionEdit({
        sectionId,
        sectionTitle: title,
        isDirty: isDraft || form.formState.isDirty,
        hasErrors: Object.keys(form.formState.errors).length > 0,
        reset: () => {
            form.reset(defaults);
            if (isDraft) onDraftDone?.();
        },
    });

    // The saved label moved on (this card's rename) — follow it while not
    // editing, so the next Edit starts from what is stored.
    const savedKey = JSON.stringify(defaults);
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the saved values themselves
    useEffect(() => {
        if (!edit.isEditing) form.reset(defaults);
    }, [savedKey, edit.isEditing]);

    const cardRef = useRef<HTMLElement>(null);
    // A draft opens in edit mode and is brought into view: it's added at the
    // end of the list, often below the fold.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
    useEffect(() => {
        if (!isDraft) return;
        edit.enterEdit();
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    async function onSubmit(values: LabelValues | KeyValues) {
        try {
            // keySchema's output carries the PEM; labelSchema's never does
            // (zod drops keys a schema doesn't name), so this is the draft.
            if ("publicKey" in values) {
                await createKey.mutateAsync(values);
                toast.success("Key added");
            } else if (contextKey) {
                // A rename sends the label only.
                await patchKey.mutateAsync({ keyId: contextKey.id, label: values.label });
                toast.success("Key renamed");
            }
            edit.exitEdit();
            if (isDraft) onDraftDone?.();
        } catch (err) {
            if (err instanceof ApiError) {
                // Field violations land on their fields; the toast says the rest.
                toastFormError(isDraft ? "Create failed" : "Save failed", form, err);
                return;
            }
            throw err;
        }
    }

    async function remove() {
        if (!contextKey) return;
        try {
            await deleteKey.mutateAsync(contextKey.id);
            toast.success("Key removed");
        } catch (err) {
            // The last key while signing is required is Precondition.Failed,
            // which friendlyMessage says as removeRefusal does.
            toastMutationError("Delete failed", err);
        } finally {
            setConfirming(false);
        }
    }

    function cancel() {
        edit.exitEdit();
        if (isDraft) onDraftDone?.();
    }

    const saving = isDraft ? createKey.isPending : patchKey.isPending;
    const actions = edit.isEditing ? (
        <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={cancel}>
                Cancel
            </Button>
            <Button
                type="button"
                size="sm"
                disabled={saving || (!isDraft && !form.formState.isDirty)}
                onClick={() => void form.handleSubmit(onSubmit)()}
            >
                Save
            </Button>
        </div>
    ) : (
        <Button type="button" variant="outline" size="sm" onClick={edit.enterEdit}>
            <Pencil aria-hidden="true" />
            Edit
        </Button>
    );

    return (
        <SectionCard ref={cardRef} id={sectionId} title={title} actions={actions}>
            {contextKey && !edit.isEditing && (
                <div className="flex flex-col gap-1 text-sm" data-testid="context-key">
                    <div>
                        <Badge tone="neutral">{contextKey.algorithm}</Badge>
                    </div>
                    <p className="truncate text-kore-text-muted">
                        <code>{contextKey.fingerprint}</code>
                    </p>
                    <p className="text-xs text-kore-text-muted">{whenUsed(contextKey)}</p>
                </div>
            )}
            {edit.isEditing && (
                <>
                    <Form form={form} onSubmit={onSubmit}>
                        <KeyFields isDraft={isDraft} />
                    </Form>
                    {contextKey && (
                        <DangerStrip isEditing={edit.isEditing}>
                            {removeRefusal && <p className="text-sm text-kore-text-muted">{removeRefusal}</p>}
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={!!removeRefusal}
                                onClick={() => setConfirming(true)}
                            >
                                Remove key
                            </Button>
                        </DangerStrip>
                    )}
                </>
            )}
            {contextKey && (
                <Confirm
                    open={confirming}
                    onOpenChange={(open) => {
                        if (!open && !deleteKey.isPending) setConfirming(false);
                    }}
                    title="Remove this key?"
                    description="Anything still signing with it stops being verified. Rotating instead means adding the new key first, then removing this one once nothing uses it."
                    variant="destructive"
                    primaryLabel={deleteKey.isPending ? "Removing…" : "Remove"}
                    confirming={deleteKey.isPending}
                    onConfirm={() => void remove()}
                />
            )}
        </SectionCard>
    );
}
